import { Format, getDate } from '@homesusa/core';
import { FieldRulesProps, FormRules } from './interfaces';
import { isNullOrEmpty, isValidMilitaryTime, getRuleErrors } from './validations';
import { ErrorMessageType } from './enums';

export const getOptionValueFromRule = (
  fieldRule: FieldRulesProps,
  optionValue: string | null | undefined
): string => {
  if (!optionValue) {
    return '';
  }
  if (fieldRule && fieldRule.options) {
    const upperValue = optionValue.toUpperCase();
    const key = Object.keys(fieldRule.options).find((opt) => opt.toUpperCase() === upperValue);
    if (key) {
      return fieldRule.options[key];
    }
  }

  return optionValue;
};

export const militaryTimeToDate = (value: string | null | undefined): Date | null => {
  let valueDate: Date | null = null;

  if (!!value && isValidMilitaryTime(value)) {
    const newDate = new Date();
    const [hours, minutes] = (value as string).split(':');
    newDate.setHours(Number(hours), Number(minutes));
    valueDate = newDate;
  }

  return valueDate;
};

export const passwordReExp = new RegExp(
  '^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[#$@!%&*?^.])[0-9a-zA-Z#$@!%&*?^.]{8,20}$'
);

export const referencedDate = (value?: Date): string => {
  const date = getDate(value);
  if (date) {
    const referencedDate = new Date(date.setHours(0, 0, 0, 0));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return referencedDate.getTime() === today.getTime() ? 'today' : Format.Date(date);
  }
  return '';
};

export const alphabetizeOptions = <Option extends { text: string }>(
  a: Option,
  b: Option
): number => {
  if (a.text.toLowerCase() < b.text.toLowerCase()) {
    return -1;
  }
  return a.text > b.text ? 1 : 0;
};

export function flattenRules(obj: object, path = ''): FormRules {
  if (typeof obj !== 'object' || obj == null) {
    return {};
  }

  if (obj.hasOwnProperty('label')) {
    return { [path]: obj as FieldRulesProps };
  }

  let flat: FormRules = {};
  for (const [key, value] of Object.entries(obj)) {
    const newPath = path !== '' ? `${path}.${key}` : key;
    const flattened = flattenRules(value, newPath);
    flat = { ...flat, ...flattened };
  }
  return flat;
}

export const getValueByPath = <TValue = unknown>(obj: object, path: string): TValue | undefined => {
  if (isNullOrEmpty(path) || obj == null) {
    return undefined;
  }

  const pathParts: string[] = path.split('.');
  let value: any = obj;
  for (const part of pathParts) {
    value = typeof value === 'object' && value != null ? value[part] : undefined;
  }
  return value;
};

export const getIdFromInputName = (inputName: string): string => inputName.replace(/\./g, '-');

export const getFieldErrors = (
  value: unknown,
  rules: FieldRulesProps,
  requiredToSubmit = false
): string[] => {
  let errors: string[] = [];
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
    const errorMessages = rules.customFieldProps?.errorMessages;
    if (rules.required) {
      errors.push(errorMessages?.required ?? `is ${ErrorMessageType.Required}`);
    } else if (requiredToSubmit && rules.requiredToSubmit) {
      errors.push(errorMessages?.requiredToSubmit ?? `is ${ErrorMessageType.RequiredToSubmit}`);
    }
  } else {
    if (requiredToSubmit) {
      const customValidations = rules.customFieldProps?.validations ?? [];
      customValidations.forEach((validation) => {
        const result = validation(value);
        if (!result.isValid) {
          errors.push(result.errorMessage ?? 'Invalid value');
        }
      });
    }

    errors = errors.concat(getRuleErrors(rules, value));
  }

  return errors;
};
